<script lang="ts">
  import { sceneGradient, type HueSceneDto } from "@piano/shared";
  import Icon from "../Icon.svelte";

  /** Szenen-Kachel mit Farbverlauf aus der Hue-Palette. */
  let {
    scene,
    pinned = null,
    compact = false,
    onclick,
  }: {
    scene: HueSceneDto;
    /** Auswahl-Modus: Haken oben rechts. */
    pinned?: boolean | null;
    compact?: boolean;
    onclick: () => void;
  } = $props();

  const glow = $derived(scene.colors[0] ?? "#FFD9A8");
  const active = $derived(scene.active);
  let pulse = $state(0);

  function tap(): void {
    pulse++;
    onclick();
  }
</script>

<button
  type="button"
  class="tile"
  class:compact
  class:active
  style:--tile-glow={glow}
  aria-pressed={pinned ?? active}
  onclick={tap}
>
  <span class="swatch" style:background={sceneGradient(scene.colors)}>
    {#key pulse}{#if pulse}<span class="ripple"></span>{/if}{/key}
  </span>
  <span class="name">{scene.name}</span>
  {#if pinned !== null}
    <span class="check" class:on={pinned}><Icon name="check" size={14} stroke={3} /></span>
  {:else if active}
    <span class="dot" aria-hidden="true"></span>
  {/if}
</button>

<style>
  .tile {
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px;
    border-radius: var(--r-lg);
    background: rgba(255, 255, 255, 0.04);
    text-align: left;
    min-width: 0;
    transition:
      background var(--dur-fast),
      box-shadow var(--dur-base) var(--ease-out),
      transform var(--dur-tap) var(--ease-out);
  }
  .tile:hover {
    background: rgba(255, 255, 255, 0.07);
  }
  .tile:active {
    transform: scale(0.95);
  }
  .tile.active {
    background: rgba(255, 255, 255, 0.08);
    box-shadow: inset 0 0 0 2px var(--tile-glow);
  }
  .swatch {
    position: relative;
    overflow: hidden;
    display: block;
    aspect-ratio: 16 / 10;
    border-radius: var(--r-md);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
    transition: box-shadow var(--dur-stage) var(--ease-out);
  }
  .active .swatch {
    box-shadow:
      inset 0 0 0 1px rgba(255, 255, 255, 0.12),
      0 6px 24px -6px var(--tile-glow);
  }
  .compact .swatch {
    aspect-ratio: 16 / 9;
  }
  .ripple {
    position: absolute;
    inset: 0;
    margin: auto;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.55);
    animation: ripple 600ms var(--ease-out) forwards;
  }
  @keyframes ripple {
    to {
      transform: scale(9);
      opacity: 0;
    }
  }
  .name {
    font-size: 13px;
    font-weight: 700;
    line-height: 1.2;
    padding: 0 2px 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .dot {
    position: absolute;
    top: 14px;
    right: 14px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.25);
  }
  .check {
    position: absolute;
    top: 13px;
    right: 13px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.35);
    color: transparent;
    box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.7);
    transition:
      background var(--dur-fast),
      color var(--dur-fast),
      transform var(--dur-base) var(--ease-pop);
  }
  .check.on {
    background: var(--accent);
    color: var(--on-accent);
    box-shadow: none;
    transform: scale(1.08);
  }
  @media (prefers-reduced-motion: reduce) {
    .ripple {
      display: none;
    }
  }
</style>
